import { useNavigate } from "@tanstack/react-router";
import { Hand, Info } from "lucide-react";
import { useEffect, useState } from "react";

import type { TVettingJobListItem, TVettingJobSource, TVettingJobStatus } from "@/api/investorVetting";
import { investorVettingService } from "@/api/investorVetting";
import { DataGrid, type TColumnDef, type TDataGridQuery, type TFilterDef } from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { formatLocaleDate } from "@/utils/date";

const PAGE_SIZE = 20;

const STATUS_OPTIONS: { value: TVettingJobStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "claimed", label: "Claimed" },
  { value: "onboarded", label: "Onboarded" },
  { value: "declined", label: "Declined" },
  { value: "auto_onboarded", label: "Auto-onboarded" },
];

const SOURCE_LABELS: Record<TVettingJobSource, string> = {
  self_registered: "Self-registered",
  invited: "Invited",
  vetting_disabled: "Vetting disabled",
};

const SUCCESS_BADGE =
  "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400";

function StatusBadge({ status }: { status: TVettingJobStatus }) {
  const styles: Record<TVettingJobStatus, string> = {
    pending: "border-border bg-muted text-muted-foreground",
    claimed: "border-primary/30 bg-primary/10 text-primary",
    onboarded: SUCCESS_BADGE,
    declined: "border-destructive/30 bg-destructive/10 text-destructive",
    auto_onboarded: SUCCESS_BADGE,
  };
  return <Badge className={styles[status]}>{status.replace("_", "-")}</Badge>;
}

type TOnboardingJobsPageProps = {
  /**
   * The admin view is the same pool defaulting to the escalated/SLA-expired
   * filter — it IS the "Admins Onboarding Job pool" (RD-6).
   */
  variant?: "sales" | "admin";
};

export function OnboardingJobsPage({ variant = "sales" }: TOnboardingJobsPageProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [reloadTick, setReloadTick] = useState(0);
  const isAdminView = variant === "admin";
  // RD-6: the admin view defaults to escalated/SLA-expired jobs — that IS the
  // "Admins Onboarding Job pool". Toggleable to see the whole pool.
  const [escalatedOnly, setEscalatedOnly] = useState(isAdminView);

  // 078: informational banner when vetting is switched off. The status
  // endpoint is any-authenticated; the caller's own `status` is ignored here.
  const [vettingDisabled, setVettingDisabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await investorVettingService.getVettingStatus();
      if (!cancelled && data?.data.vetting_enabled === false) {
        setVettingDisabled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const detailPath = (jobId: string) =>
    isAdminView
      ? navigate({ to: "/app/admin/investor-onboarding-jobs/$job_id", params: { job_id: jobId } })
      : navigate({ to: "/app/sales/onboarding-jobs/$job_id", params: { job_id: jobId } });

  const fetcher = async (query: TDataGridQuery) => {
    const status = query.filters.status;
    const slaExpired = query.filters.sla_expired;
    const mine = query.filters.mine;
    const { data, error } = await investorVettingService.getJobs(
      {
        limit: query.limit,
        offset: query.offset,
        ...(typeof status === "string" && status ? { status: status as TVettingJobStatus } : {}),
        ...(typeof slaExpired === "boolean" ? { sla_expired: slaExpired } : escalatedOnly ? { sla_expired: true } : {}),
        ...(typeof mine === "boolean" && mine ? { mine } : {}),
        // Oldest first by default (per spec); DataGrid sorting overrides.
        sort_by: (query.sort_by as "created_at") ?? "created_at",
        sort_order: query.sort_order ?? "asc",
      },
      { signal: query.signal }
    );
    if (error) throw error;
    return data;
  };

  const handleClaim = async (job: TVettingJobListItem) => {
    const { error } = await investorVettingService.claimJob(job.id);
    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to take ownership",
        description: await extractErrorMessage(error),
      });
      setReloadTick((t) => t + 1);
      return;
    }
    toast({ title: "Job claimed", description: "You now own this onboarding job." });
    void detailPath(job.id);
  };

  const columns: TColumnDef<TVettingJobListItem>[] = [
    {
      id: "investor",
      header: "Investor",
      type: "user",
      sortable: false,
      accessor: (job) => ({ name: job.investor_name, email: job.investor_email }),
    },
    {
      id: "source",
      header: "Source",
      accessor: (job) => SOURCE_LABELS[job.source] ?? job.source,
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortKey: "status",
      accessor: (job) => job.status,
      cell: (job) => <StatusBadge status={job.status} />,
    },
    {
      id: "created_at",
      header: "Created",
      type: "relativeTime",
      sortable: true,
      sortKey: "created_at",
      accessor: (job) => job.created_at,
    },
    {
      id: "sla_deadline",
      header: "SLA deadline",
      sortable: true,
      sortKey: "sla_deadline",
      accessor: (job) => job.sla_deadline,
      cell: (job) =>
        job.sla_expired ? (
          <Badge className="border-destructive/30 bg-destructive/10 text-destructive">
            Expired {formatLocaleDate(new Date(job.sla_deadline))}
          </Badge>
        ) : (
          <span className="text-muted-foreground">{formatLocaleDate(new Date(job.sla_deadline))}</span>
        ),
    },
    {
      id: "owner",
      header: "Owner",
      accessor: (job) => job.claimed_by_name ?? "—",
    },
    {
      id: "actions",
      header: "",
      pinRight: true,
      accessor: (job) => job.id,
      cell: (job) => {
        const claimable = job.status === "pending" || (job.status === "auto_onboarded" && !job.claimed_by);
        return (
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void detailPath(job.id)}
            >
              View
            </Button>
            {claimable && (
              <Button
                size="sm"
                onClick={() => void handleClaim(job)}
              >
                <Hand className="mr-1.5 h-3.5 w-3.5" />
                Take ownership
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const filters: TFilterDef[] = [
    { id: "status", type: "select", label: "Status", options: STATUS_OPTIONS },
    { id: "sla_expired", type: "boolean", label: "SLA expired" },
    { id: "mine", type: "boolean", label: "Mine" },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
            {isAdminView ? "Investor Onboarding Jobs" : "Investor Onboarding"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isAdminView
              ? "Escalated jobs are shown by default — clear the SLA filter to see the whole pool."
              : "Take ownership of a job to see the investor's contact details and process their onboarding."}
          </p>
        </div>

        {vettingDisabled && (
          <div
            role="status"
            data-testid="vetting-disabled-banner"
            className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-foreground"
          >
            <Info className="h-4 w-4 flex-shrink-0 text-primary" />
            Investor vetting is currently disabled — new investors are onboarded automatically and no new jobs arrive in
            this pool.
          </div>
        )}

        {isAdminView && (
          <div className="flex items-center gap-2">
            <Button
              variant={escalatedOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setEscalatedOnly((value) => !value)}
            >
              {escalatedOnly ? "Showing SLA-expired jobs" : "Showing all jobs"}
            </Button>
          </div>
        )}

        <DataGrid<TVettingJobListItem>
          getRowId={(row) => row.id}
          persistKey={isAdminView ? "admin.investorOnboardingJobs" : "sales.onboardingJobs"}
          ariaLabel="Investor onboarding jobs"
          data={{ mode: "server", fetcher, deps: [reloadTick, escalatedOnly] }}
          columns={columns}
          filters={filters}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50] }}
          toolbar={{ refresh: true, columnVisibility: true, density: true }}
          emptyState={{ title: "No onboarding jobs in the pool." }}
        />
      </div>
    </div>
  );
}
